import { useCallback, type Dispatch, type FocusEvent, type SetStateAction } from 'react'
import type { EnglishTestQuestionDto } from '@/api/generated/models'
import type { QuestionAnswerValue } from './types'
import { isTextEntryQuestion } from './questionUtils'

type UseEnglishTestAnswerHandlersArgs = {
  answers: QuestionAnswerValue[]
  handleSubmissionError: (error: unknown) => void
  questions: EnglishTestQuestionDto[]
  setAnswers: Dispatch<SetStateAction<QuestionAnswerValue[]>>
  setCurrentGroupIndex: Dispatch<SetStateAction<number>>
  setSubmissionError: (error: string | null) => void
  submitMultipleChoiceAnswer: (
    question: EnglishTestQuestionDto,
    optionIndex: number
  ) => Promise<void>
  submitTextAnswer: (question: EnglishTestQuestionDto, value: string) => Promise<void>
  flushVisibleTextAnswers: () => Promise<void>
}

export function useEnglishTestAnswerHandlers({
  answers,
  handleSubmissionError,
  questions,
  setAnswers,
  setCurrentGroupIndex,
  setSubmissionError,
  submitMultipleChoiceAnswer,
  submitTextAnswer,
  flushVisibleTextAnswers
}: UseEnglishTestAnswerHandlersArgs) {
  const handleAnswerSelect = useCallback(
    (questionIndex: number, optionIndex: number) => {
      setSubmissionError(null)
      setAnswers(currentAnswers =>
        currentAnswers.map((answer, index) =>
          index === questionIndex ? { type: 'option', optionIndex } : answer
        )
      )

      const question = questions[questionIndex]

      if (!question || isTextEntryQuestion(question)) {
        return
      }

      void submitMultipleChoiceAnswer(question, optionIndex).catch(handleSubmissionError)
    },
    [handleSubmissionError, questions, setAnswers, setSubmissionError, submitMultipleChoiceAnswer]
  )

  const handleTextAnswerChange = useCallback(
    (questionIndex: number, value: string) => {
      setSubmissionError(null)
      setAnswers(currentAnswers =>
        currentAnswers.map((answer, index) =>
          index === questionIndex ? { type: 'text', value } : answer
        )
      )
    },
    [setAnswers, setSubmissionError]
  )

  const handleTextAnswerBlur = useCallback(
    (questionIndex: number, event: FocusEvent<HTMLInputElement>) => {
      const nextFocusedElement = event.relatedTarget

      if (
        nextFocusedElement instanceof HTMLElement &&
        nextFocusedElement.closest('[data-assessment-action="advance"]')
      ) {
        return
      }

      const question = questions[questionIndex]
      const selectedAnswer = answers[questionIndex]

      if (!question || !isTextEntryQuestion(question) || selectedAnswer?.type !== 'text') {
        return
      }

      void submitTextAnswer(question, selectedAnswer.value).catch(handleSubmissionError)
    },
    [answers, handleSubmissionError, questions, submitTextAnswer]
  )

  const handleDateAnswerChange = useCallback(
    (question: EnglishTestQuestionDto, questionIndex: number, value: string) => {
      handleTextAnswerChange(questionIndex, value)
      void submitTextAnswer(question, value).catch(handleSubmissionError)
    },
    [handleSubmissionError, handleTextAnswerChange, submitTextAnswer]
  )

  const handleContinue = useCallback(async () => {
    setSubmissionError(null)

    try {
      await flushVisibleTextAnswers()
      setCurrentGroupIndex(index => index + 1)
    } catch (error) {
      handleSubmissionError(error)
    }
  }, [flushVisibleTextAnswers, handleSubmissionError, setCurrentGroupIndex, setSubmissionError])

  return {
    handleAnswerSelect,
    handleContinue,
    handleDateAnswerChange,
    handleTextAnswerBlur,
    handleTextAnswerChange
  }
}
